import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { truncate } from 'lodash'
import { useTranslation } from 'react-i18next'

import { AllergenTags } from '@src/components/AllergenTags/AllergenTags'
import { ItemCounter } from '@src/components/ItemCounter/ItemCounter'
import { SpicyChiliSVG } from '@src/components/SVGS/SpicyChiliSVG'
import { useBasketItems } from '@src/hooks/useBasketItems/useBasketItems'
import { useMenuItemQueryParam } from '@src/hooks/useMenuItemQueryParam'
import { MAXIMUM_DESCRIPTION_LENGTH } from '@src/pages/OutletPage/OutletMenu/MenuItem/MAX_DESCRIPTION_LENGTH.constants'
import { imageJitURL } from '@src/utils/imageJitURL'

import {
  ExtraDetailsContainer,
  ImageContainer,
  ItemDetailsContainer,
  ItemDetailsHeader,
  Container,
  FlexRowContainer,
  MenuDescription,
  AllergenFavouriteContainer,
  MenuItemCounter,
  MenuItemCounterText,
  ItemDetailsHeaderAndCounter,
  SoldOutText,
  ChiliContainer,
} from './MenuItem.styles'
import { TreeOutletMenuItemGroup } from '../../menuItemGroupTreeType'
import { GetOutletMenuItemDocument } from './queries/__generated__/GetOutletMenuItem.graphql-interface'
import { useOutletPageContext } from '../../OutletPageContext'

export const MenuItem: React.FC<{
  outletId: string
  menuItem?: TreeOutletMenuItemGroup['outletMenuItems'][0]
  menuItemId: string
  finalElement: boolean
  showMenuThumbnails: boolean
  dataTestId?: string
}> = ({
  outletId,
  menuItem,
  menuItemId,
  finalElement,
  showMenuThumbnails,
  dataTestId,
}) => {
  const [, setItemId] = useMenuItemQueryParam()
  const basketItems = useBasketItems()
  const { t } = useTranslation('menuItem')
  const { isStackedMenu } = useOutletPageContext()

  const menuItemCount = useMemo(
    () =>
      basketItems.items
        .filter(
          item =>
            item.menuItemId === menuItemId &&
            item.outletMenuItemId.includes(outletId)
        )
        .reduce((sum, item) => sum + item.quantity, 0),
    [basketItems.items, menuItemId, outletId]
  )

  const { data, error } = useQuery(GetOutletMenuItemDocument, {
    variables: { outletId, menuItemId },
    skip: !!menuItem,
  })

  if ((!data && !menuItem) || error) return null

  const resolvedMenuItem = data?.outletMenuItem || menuItem!

  const {
    name,
    price,
    allergens,
    image,
    description,
    soldOut,
    ageRestricted,
    spiceLevel,
  } = resolvedMenuItem

  const longDescription = truncate(description || undefined, {
    length: MAXIMUM_DESCRIPTION_LENGTH,
    separator: ' ',
    omission: ' ...',
  })

  return (
    <Container
      onClick={() =>
        setItemId(menuItemId, isStackedMenu ? 'push' : 'replaceIn')
      }
      isDisabled={!!soldOut}
      finalElement={finalElement}
      data-test-id={dataTestId}
      title={name}
    >
      <FlexRowContainer isDisabled={!!soldOut}>
        <ItemDetailsContainer isDisabled={!!soldOut}>
          <div>
            <ItemDetailsHeaderAndCounter>
              <ItemDetailsHeader>{name}</ItemDetailsHeader>
              {spiceLevel > 0 && (
                <ChiliContainer>
                  {Array.from({ length: spiceLevel }).map((_, index) => (
                    <SpicyChiliSVG width={15} height={15} key={index} />
                  ))}
                </ChiliContainer>
              )}
              {(!image || !showMenuThumbnails) && menuItemCount !== 0 && (
                <MenuItemCounter>
                  <MenuItemCounterText>{menuItemCount}</MenuItemCounterText>
                </MenuItemCounter>
              )}
            </ItemDetailsHeaderAndCounter>
            {description && (
              <MenuDescription>{longDescription}</MenuDescription>
            )}
          </div>
          <ExtraDetailsContainer>
            {soldOut ? (
              <SoldOutText> {t('sold_out')}</SoldOutText>
            ) : (
              <ItemCounter
                price={price}
                count={0} // Prevents rendering of unfinished plus/minus buttons if count is above 0 and always opens up modal instead
                isDisabled={!!soldOut}
                isMenuItem
              />
            )}

            <AllergenFavouriteContainer isDisabled={!!soldOut}>
              {(allergens || !!ageRestricted) && (
                <AllergenTags
                  ageRestricted={!!ageRestricted}
                  allergens={allergens || []}
                  iconView
                  isMenuitem
                />
              )}
            </AllergenFavouriteContainer>
          </ExtraDetailsContainer>
        </ItemDetailsContainer>

        {image && showMenuThumbnails && (
          <ImageContainer
            imageUrl={imageJitURL(image, {
              resize: {
                width: 128,
                height: 128,
                fit: 'cover',
              },
              toFormat: 'jpeg',
            })}
          >
            {menuItemCount !== 0 && (
              <MenuItemCounter>
                <MenuItemCounterText>{menuItemCount}</MenuItemCounterText>
              </MenuItemCounter>
            )}
          </ImageContainer>
        )}
      </FlexRowContainer>
    </Container>
  )
}
